import React, { useEffect, useState } from 'react';
import InputSkeletonGroup from '../../components/Common/Loaders/InputSkeleton';
import DisplaySection from '../../components/MoneyTransfer/DisplaySection';
import { DisclaimerType } from '../../types/api/moneyTransfer';
import { DisplaySectionType } from '../../types/components';
import { GetRRTransferDetailsResponse } from '../../types/api/card';
import { useHistory } from 'react-router-dom';
import { ROUTES as API_ROUTES } from '../../constants/api';
import i18n from '../../i18n/config';
import { RRSDKTransferDetailsProps } from '../../types/props';
import { GetApiData, GetApiReceiptData } from '../../api/general';

import FlowScreen from '../../components/Layouts/FlowScreen';
import PageBlock from '../../components/Layouts/PageBlock';
import ActionRow from '../../components/Layouts/ActionRow';
import ActionLink from '../../components/Common/ActionLink';
import AlertModal from '../../components/Common/Modals/AlertModal';
import { TRANSFER_METHODS } from '../../constants/enums';


const RRSDKTransferDetails = (props: RRSDKTransferDetailsProps) => {
  const history = useHistory();
  const [sections, setSections] = useState<DisplaySectionType[]>([]);
  const [disclaimers, setDisclaimers] = useState<DisclaimerType[]>([]);
  const [receipt, setReceipt] = useState<{ downloadReceiptText: string, downloadReceiptUrl: string }>();
  const [footer, setFooter] = useState<string>("");
  const [transferId, setTransferId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const getTransferDetails = async () => {
      try {
        const transferId = props.match?.params.transferId ?? "";
        const { result } = await GetApiData<GetRRTransferDetailsResponse>(API_ROUTES.CARD.GET_RR_SDK_TRANSFER_DETAILS(transferId, props.transferMethod));
        setSections(result.displaySections);
        setDisclaimers(result.footer.disclaimers);
        setReceipt({ downloadReceiptText: result.downloadReceiptText, downloadReceiptUrl: result.downloadReceiptUrl });
        setFooter(result.footer.content ?? "");
        setTransferId(transferId);
        setLoading(false);

      } catch (err) {
        if (err?.data?.errors) {
          setErrorMessage(err.data.errors[0].errorMessage);
        } else {
          setErrorMessage(i18n.t("COMMON.ERROR"));
        }
        setLoading(false);
        setShowErrorModal(true);
      }
    }

    getTransferDetails()
  }, []);

  const handleGetReceipt = async () => {
    try {
      const filename = getReceiptFilename(transferId);
      await GetApiReceiptData<string>(receipt?.downloadReceiptUrl ?? "", filename);
    } catch (err) {
      if (err?.data?.errors) {
        setErrorMessage(err.data.errors[0].errorMessage);
      } else {
        setErrorMessage(i18n.t("COMMON.ERROR"));
      }
      setLoading(false);
      setShowErrorModal(true);
    }
  }

  /**
   * Get the filename for the receipt based on the transfer method
   * @param {string} transferId
   */
  const getReceiptFilename = (transferId: string) => {
    let filename: string;

    switch (props.transferMethod) {
      case TRANSFER_METHODS.PUSH_TO_CARD:
        filename = `Brightwell_Send_To_Debit_Card_${transferId}.pdf`
        break;
      case TRANSFER_METHODS.TO_ACCOUNT:
        filename = `Send_With_Brightwell_${transferId}.pdf`
        break;
      case TRANSFER_METHODS.MOBILE_WALLET:
        filename = `Brightwell_Mobile_Wallet_${transferId}.pdf`
        break;
      default:
        filename = `Brightwell_Transfer_${transferId}.pdf`;
    }

    return filename;
  }

  /**
   * Build the disclaimers to display
   * @param {*} disclaimers
   */
  const renderDisclaimers = (disclaimers: DisclaimerType[]) => {
    if (disclaimers?.length > 0) {
      return disclaimers.map((d) => {
        return (
          <p key={d.disclaimerId} className="x-small disclaimer marengo-grey">
            <sup>{d.disclaimerId} </sup>
            {d.text}
          </p>
        );
      });
    }
  }

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.MY_CARD.RR_SDK_TRANSFER_DETAILS.TITLE")}
      className="receipt display-section-wrapper"
    >
      <PageBlock>
        <PageBlock.Loader loadingContent={loading} >
          <InputSkeletonGroup numInputs={4} />
        </PageBlock.Loader>
        <PageBlock.Body loadingContent={loading}>
          <DisplaySection sections={sections} variant="transparent" className="spacing-bottom-small" />
          {renderDisclaimers(disclaimers)}
          <div dangerouslySetInnerHTML={{ __html: footer }} />
          <ActionRow>
            <ActionRow.Forward>
              <ActionLink
                clickFunc={handleGetReceipt}
              >
                {receipt?.downloadReceiptText}
              </ActionLink>
            </ActionRow.Forward>
            <ActionRow.Backward>
              <ActionLink
                classes="spacing-none"
                clickFunc={() => history.goBack()}
              >
                <div className="glyphicon glyphicon-menu-left link_icon"></div>
                {i18n.t("COMMON.BACK")}
              </ActionLink>
            </ActionRow.Backward>
          </ActionRow>

        </PageBlock.Body>
      </PageBlock>

      <AlertModal
        onClose={() => setShowErrorModal(false)}
        open={showErrorModal}
        title={i18n.t("COMMON.ERROR_MODAL_HEADING")}
        content={errorMessage}
      />
    </FlowScreen>
  );
}

export default RRSDKTransferDetails;